import * as THREE from 'three';
import React, { useEffect, useMemo, useRef } from 'react';
import { useGLTF } from '@react-three/drei';
import { GLTFModel, HandSize, ModelData, Vector } from '../Interfaces';
import { IUseGlbAnimations } from '../useGlbAnimations';
import { useFrame } from '@react-three/fiber';
import { getDirectModelData, getMirrorModelData } from '../constants';

type RootBlockProps = {
  rotation: Vector;
  position: Vector;
  scale: THREE.Vector3;
};

interface Props {
  mirrored?: boolean;
  color: string;
  animator: IUseGlbAnimations;
  size?: HandSize;
}

export default function HandsModel({
  mirrored,
  color,
  animator,
  size = 'big'
}: Props) {
  const group = useRef<THREE.Group | null>(null);
  const mixer = useRef<THREE.AnimationMixer>();
  const { nodes, materials, animations } = useGLTF(
    mirrored ? '/assets/models/hands.glb' : '/assets/models/hands_clone.glb'
  ) as GLTFModel;

  useEffect(() => {
    if (animations && group.current && !animator.getIsActivated()) {
      const newMixer = animator.activate(animations, group.current);
      mixer.current = newMixer;
      if (newMixer) {
        animator.toShake();
      }
    }
  }, [animations, group, animator]);

  useFrame((state, delay) => {
    const mixerInstance = mixer.current;
    if (mixerInstance) {
      animator.everyFrame();
      mixerInstance.update(delay);
    }
  });

  const material = useMemo<THREE.MeshStandardMaterial>(() => {
    const m = materials.lambert1.clone();
    m.setValues({ color });
    return m;
  }, [materials, color]);

  const geometry = useMemo(() => {
    return nodes.head_4.geometry.clone();
  }, [nodes]);

  const skeleton = useMemo(() => {
    return nodes.head_4.skeleton.clone();
  }, [nodes]);

  const rootProps = useMemo<RootBlockProps>(() => {
    const getProps = (obj: ModelData): RootBlockProps => {
      return {
        rotation: obj.rotation,
        position: obj.position,
        scale: new THREE.Vector3(...obj.scale)
      };
    };
    const modelData = mirrored
      ? getMirrorModelData(size)
      : getDirectModelData(size);
    return getProps(modelData);
  }, [mirrored, size]);

  return (
    <group ref={group} {...rootProps} dispose={null}>
      <group>
        <group
          name='FBXASC049'
          position={[0, -0.03, -0.01]}
          rotation={[0, 0.26, 0]}
          scale={1.05}
        >
          <group name='head' />
        </group>
        <group
          name='FBXASC050'
          position={[0.02, 0.62, 0.01]}
          rotation={[0, 0.26, 0]}
          scale={1.47}
        >
          <group name='head_3'>
            <skinnedMesh
              name={`head_4${mirrored ? '_mirrored' : ''}`}
              geometry={geometry}
              material={material}
              skeleton={skeleton}
            />
          </group>
        </group>
        <group name='FBXASC051' position={[-0.04, -0.08, 0.07]}>
          <group
            name='Subdivision_Surface_1'
            position={[0.05, 0.08, -0.07]}
            rotation={[0, 0.26, 0]}
          />
        </group>
        <primitive object={nodes.root} />
      </group>
    </group>
  );
}
